
import React from 'react';
import { navigate } from "gatsby"
import { omit } from 'lodash';
import { useFormik } from 'formik';
import { ErrorMessage, Button, Label, Input, Checkbox, DateInput } from '../../../components/form';
import { PatientCareUserCreateRequest } from '../../../api/patient-care/model/PatientCareUserCreateRequest';
import { PatientCareStaquisService } from '../../../api/patient-care/service/PatientCareStaquisService';
import { UrlService } from '../../../core/services/UrlService';
import Layout from '../../../components/layout';
import ReferenceListSection from '../../../components/reference-list/ReferenceListSection';
import references from './data/reference.json';
import Seo from '../../../components/seo';
import dayjs from 'dayjs';
import { TrackingService } from '../../../core/services/TrackingService';
import './style.scss';

type FormValues = PatientCareUserCreateRequest & {
    email_confirm: string;
    guardian_mobile: string;
    guardian_name: string;
    is_agree_terms_and_condition: boolean;
};

type Props = {
    location: Location,
};

const StaquisSubsidyRegistrationPage: React.FC<Props> = ({ location }) => {
    const params = new URLSearchParams(location.search);
    const utm = {
        utm_content: params.get("utm_content"),
        utm_medium: params.get("utm_medium"),
        utm_source: params.get("utm_source"),
        utm_campaign: params.get("utm_campaign"),       
    };

    const [errorMsg, setErrorMsg] = React.useState<string>('');
    const { values, handleSubmit, handleChange } = useFormik<FormValues>({
        initialValues: {
            name_en: '',
            name_zh: '',
            email: '',
            mobile: '',
            dialing_code: '+852',
            dob: '',
            guardian_name: '',
            guardian_mobile: '',
            partial_hkid: '',
            phase: 'phase-1',
            email_confirm: '',
            is_agree_terms_and_condition: false,
        },
        onSubmit: async (val: FormValues) => {
            if (val.email !== val.email_confirm) {
                setErrorMsg('請確認您的電郵地址。');
                return;
            }

            if (dayjs().diff(dayjs(values.dob), 'year') < 18) {
                if (values.guardian_name === '') {
                    setErrorMsg('請提供監護人姓名。');
                    return;
                }
                if (values.guardian_mobile === '') {
                    setErrorMsg('請提供監護人聯絡電話。');
                    return;
                }
            }

            try {
                const params = omit(val, ['is_agree_terms_and_condition', 'email_confirm']);
                const res = await PatientCareStaquisService.create({
                    ...params,
                    utm,
                });
                TrackingService.trackCustomEvent({
                    category: 'patient-care',
                    action: 'registration',
                    label: 'staquis',
                    params: {
                        campaign_name: 'Staquis',
                        step: 'registration',
                    }
                });
                setErrorMsg('');
                navigate(`/patient-care/staquis/clinics?u=${res.uuid}`, { replace: true });
            } catch (err) {
                setErrorMsg(err.message);
            }
        },
    });

    const isAllFieldsFilled = React.useMemo(() => {
        const val = omit(values, "guardian_name", "guardian_mobile")
        return Object.values(val).every((v) => !!v);
    }, [values]);

    return (
        <Layout removeNav={true} endDate={new Date('2022-01-01')} endHref="/patient-care/staquis/end">
            <Seo title="FindDoc 立即登記體驗無類固醇外用濕疹藥膏" description="FindDoc 立即登記體驗無類固醇外用濕疹藥膏" ogImage={UrlService.getLocalImageUrl('patient-care/staquis/banner.png')}></Seo>
            <div className="subsidy-programme patient-care_staquis">
                <section className="masthead" role="img" aria-label="FindDoc 立即登記體驗無類固醇外用濕疹藥膏">
                    <img style={{ content: `https://${UrlService.getLocalImageUrl('patient-care/staquis/banner.png')}` }} />
                </section>

                <section className="content">
                    <div className='container'>
                        <div className="text-content">
                            <div className="row intro">
                                <div className="col">
                                    <h3>什麼是濕疹？</h3>
                                    <p className="text-secondary-text">
                                        濕疹是不具傳染性的皮膚疾病<sup>1</sup>，患者身體的每個部位都可能會出現濕疹，而濕疹症狀亦因人而異<sup>2</sup>，其中包括皮膚痕癢乾燥、紅腫、結痂脫屑等，患者抓癢時皮膚更可能出現滲水或流血<sup>1,3</sup>
                                    </p>
                                </div>
                            </div>
                            <div className="row intro">
                                <div className="col">
                                    <h3>STAQUIS™是什麼？</h3>
                                    <p className="text-secondary-text">
                                        STAQUIS™是近十年來第一款由美國食品藥物管理局獲准使用於濕疹的處方外用藥物<sup>4,5</sup>，其配方適合患有輕度至中度濕疹的兩歲或以上的人士使用<sup>6</sup>。
                                    </p>
                                    <p className="text-secondary-text">
                                        科研證實STAQUIS™最快4日即可紓緩皮膚痕癢<sup>7</sup>，8日便可改善濕疹症狀<sup>8</sup>，更有臨床研究結果顯示STAQUIS™副作用較輕微<sup>9</sup>，長期使用亦非常安心！
                                    </p>
                                    <p className="text-secondary-text">
                                        FindDoc 快德香港現聯乘Pfizer HK輝瑞香港，為符合申請資格人士提供免費STAQUIS™ 60克試用裝，申請只需簡單4個步驟，立即填寫資料，一起對抗濕疹！
                                    </p>
                                    <p className="text-secondary-text">
                                        ** STAQUIS™屬處方藥物，成效及副作用因人而異，須由醫生判斷您的情況，以決定您是否適用此藥膏。此計劃包括免費STAQUIS™ 60g試用裝乙支，您仍需繳付醫生診療費或其他診所消費。
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="row step-and-form">
                            <div className="col-lg-6">
                                <p className="important">
                                    計劃開始日期：即日起至 2021年12月31日
                                </p>
                                <p className="important">
                                    申請者條件：
                                </p>
                                <ol>
                                    <li>持有有效香港永久居民身份證。</li>
                                    <li>已年滿兩歲。</li>
                                    <li>由醫生確診為輕至中度濕疹患者</li>
                                </ol>
                                <p className="important">
                                    申請流程：
                                </p>
                                <ol>
                                    <li>填寫右方表格，按下一步選擇診所，並完成登記。</li>
                                    <li>成功登記後，您的電子換領券會在一個工作天內發送至您的郵箱。</li>
                                    <li>電子換領券印有您所選擇的診所資料，您須在成功登記後的30天內，致電診所進行預約，並到診所完成看診。</li>
                                    <li>於診所付款時，請向醫生或護士出示您的電子換領券。</li>
                                </ol>
                                <p className="important">
                                    申請者注意事項：
                                </p>
                                <ol>
                                    <li>每人只可提交申請一次。</li>
                                    <li>申請成功的電子換領券只適用於您已選擇的指定診所（診所資料會於券上列明）。</li>
                                    <li>STAQUIS™屬處方藥物，醫生會為您的情況決定您是否適合使用，您須被處方STAQUIS™濕疹藥膏，方可將此券作換領使用。</li>
                                    <li>此計劃不包括醫生診療費或其他診所消費。</li>
                                    <li>若您並不適用此藥膏，您或需為其他藥物付款。</li>
                                    <li>電子換領券必須在券上列明的有效期內使用，逾期無效。</li>
                                    <li>FindDoc 快德香港將保留任何爭議的最終決定權，包括隨時更改活動及其條款及細則，而毋須另行通知。</li>
                                </ol>
                            </div>
                            <div className="col-lg-6 application-form-col">
                                <div>
                                    <div className="application-form-title">
                                        請於以下表格填寫看診病人的資料:
                                    </div>
                                    <form className="application-form" onSubmit={handleSubmit}>
                                        <div className="form-group">
                                            <Label
                                                htmlFor="name_en"
                                            >
                                                身份證上的英文姓名
                                        </Label>
                                            <Input
                                                id="name_en"
                                                name="name_en"
                                                type="text"
                                                onChange={handleChange}
                                                value={values.name_en}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Label htmlFor="name_zh">
                                                身份證上的中文姓名
                                        </Label>
                                            <Input
                                                id="name_zh"
                                                name="name_zh"
                                                type="text"
                                                onChange={handleChange}
                                                value={values.name_zh}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Label htmlFor="email">
                                                您的電郵地址
                                        </Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                onChange={handleChange}
                                                value={values.email}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Label htmlFor="email">
                                                請再次確認電郵地址
                                        </Label>
                                            <Input
                                                id="email_confirm"
                                                name="email_confirm"
                                                type="email_confirm"
                                                onChange={handleChange}
                                                value={values.email_confirm}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Label htmlFor="mobile">
                                                手機號碼
                                        </Label>
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <span className="input-group-text">{values.dialing_code}</span>
                                                </div>
                                                <Input
                                                    id="mobile"
                                                    name="mobile"
                                                    type="text"
                                                    onChange={handleChange}
                                                    value={values.mobile}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <Label htmlFor="dob">
                                                出生日期 <span className="mini">(年-月-日)</span>
                                            </Label>
                                            <DateInput
                                                id="dob"
                                                name="dob"
                                                onChange={handleChange}
                                                value={values.dob}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Label htmlFor="partial_hkid">
                                                香港身份證號碼 (首 4 個號碼, 例: A123)
                                        </Label>
                                            <Input
                                                id="partial_hkid"
                                                name="partial_hkid"
                                                type="text"
                                                onChange={handleChange}
                                                value={values.partial_hkid}
                                                required
                                                maxLength={4}
                                            />
                                        </div>
                                        {
                                            values.dob && (dayjs().diff(dayjs(values.dob), 'year') < 18) && (
                                                <>
                                                    <span>
                                                        <u>
                                                            若您未滿十八歲，請填寫您的監護人資料：
                                                    </u>
                                                    </span>
                                                    <div className="form-group">
                                                        <Label
                                                            htmlFor="name_en"
                                                        >
                                                            監護人姓名
                                                    </Label>
                                                        <Input
                                                            id="guardian_name"
                                                            name="guardian_name"
                                                            type="text"
                                                            onChange={handleChange}
                                                            value={values.guardian_name}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Label htmlFor="guardian_mobile">
                                                            監護人聯絡電話
                                                    </Label>
                                                        <div className="input-group">
                                                            <div className="input-group-prepend">
                                                                <span className="input-group-text">{values.dialing_code}</span>
                                                            </div>
                                                            <Input
                                                                id="guardian_mobile"
                                                                name="guardian_mobile"
                                                                type="text"
                                                                onChange={handleChange}
                                                                value={values.guardian_mobile}
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        }
                                        <Checkbox
                                            onChange={handleChange}
                                            name="is_agree_terms_and_condition"
                                            id="is_agree_terms_and_condition"
                                            labelText={`我已閱讀及同意FindDoc的<a href="${UrlService.getBaseUrl('/terms')}">使用條款</a>和<a href="${UrlService.getBaseUrl('/privacy')}">私隱政策</a>。`}
                                        />
                                        <div className="form-group">
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                data-ga-event-action="react-example-event-action"
                                                data-ga-event-label="react-example-event-label"
                                                data-ga-event-category="react-example-event-category"
                                                disabled={!isAllFieldsFilled}
                                            >
                                                下一步
                                        </Button>
                                        </div>
                                        {errorMsg && <ErrorMessage message={errorMsg} shouldShowIcon={true} />}
                                    </form>

                                </div>
                            </div>
                        </div>
                        <ReferenceListSection references={references} />
                    </div>
                </section>
                <section className="approval-code">
                    <div className="container">PP-STA-HKG-0133</div>
                </section>
            </div>
        </Layout>
    );
};

export default StaquisSubsidyRegistrationPage;
